import React from 'react';
import { Alert, Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { TableSurface, headerCellSx, bodyRowSx, TableEmptyState } from '../../common/pageKit';
import { formatINR, formatNumber, formatPct } from '../../../dashboard/primitives';
import { SalesMargin } from '../../../types/salesAnalytics';

interface MarginSectionProps {
  margin: SalesMargin | null;
  loading: boolean;
}

const numCellSx = { fontVariantNumeric: 'tabular-nums' as const, textAlign: 'right' as const };

/**
 * Margin available for {includedLineCount} of {totalLineCount} invoice
 * lines. Historical invoices predate cost capture (see SalesAnalytics.js's
 * getMargin) so coverage can legitimately be 0% — the two tables below only
 * ever include lines that carry a captured cost, so an empty table here means
 * "no covered lines," never "no margin." Rendering a chart of a subset this
 * small would visually imply completeness that doesn't exist, so this stays
 * tabular rather than a bar chart.
 */
const MarginSection: React.FC<MarginSectionProps> = ({ margin, loading }) => {
  if (loading || !margin) {
    return <Box sx={{ minHeight: 200, mb: 3 }} />;
  }

  const { coverage } = margin;
  const zeroCoverage = coverage.includedLineCount === 0;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Margin</Typography>

      <Alert severity={zeroCoverage ? 'warning' : coverage.coveragePct !== null && coverage.coveragePct < 50 ? 'warning' : 'info'} sx={{ mb: 2, borderRadius: 2 }}>
        {coverage.totalLineCount === 0 ? (
          <>No invoice lines in this period — margin cannot be computed.</>
        ) : (
          <>
            Margin available for <strong>{formatNumber(coverage.includedLineCount)}</strong> of{' '}
            <strong>{formatNumber(coverage.totalLineCount)}</strong> invoice lines this period (
            {coverage.coveragePct === null ? '—' : formatPct(coverage.coveragePct)}). The remaining{' '}
            <strong>{formatNumber(coverage.excludedLineCount)}</strong> line{coverage.excludedLineCount === 1 ? '' : 's'} predate
            cost capture and are excluded from every figure below — they are not treated as zero-cost or zero-margin.
          </>
        )}
      </Alert>

      {!zeroCoverage && (
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <SummaryStat label="Covered revenue" value={formatINR(margin.overall.revenue)} />
          <SummaryStat label="Covered cost" value={formatINR(margin.overall.cost)} />
          <SummaryStat label="Covered margin" value={formatINR(margin.overall.margin)} />
          <SummaryStat
            label="Covered margin %"
            value={margin.overall.marginPct === null ? '—' : formatPct(margin.overall.marginPct)}
          />
        </Box>
      )}

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            By Product
          </Typography>
          <TableSurface>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Product</TableCell>
                    <TableCell sx={headerCellSx} align="right">Revenue</TableCell>
                    <TableCell sx={headerCellSx} align="right">Margin</TableCell>
                    <TableCell sx={headerCellSx} align="right">Margin %</TableCell>
                    <TableCell sx={headerCellSx} align="right">Lines</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {margin.byProduct.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ border: 0 }}>
                        <TableEmptyState title="No cost-captured lines" hint="No product lines in this period carry a captured cost yet." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    margin.byProduct.map((row) => (
                      <TableRow key={row.product_id} sx={bodyRowSx}>
                        <TableCell>{row.product_name}</TableCell>
                        <TableCell sx={numCellSx}>{formatINR(row.revenue)}</TableCell>
                        <TableCell sx={numCellSx}>{formatINR(row.margin)}</TableCell>
                        <TableCell sx={numCellSx}>{row.marginPct === null ? '—' : formatPct(row.marginPct)}</TableCell>
                        <TableCell sx={numCellSx}>{formatNumber(row.lineCount)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </TableSurface>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            By Customer
          </Typography>
          <TableSurface>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Customer</TableCell>
                    <TableCell sx={headerCellSx} align="right">Revenue</TableCell>
                    <TableCell sx={headerCellSx} align="right">Margin</TableCell>
                    <TableCell sx={headerCellSx} align="right">Margin %</TableCell>
                    <TableCell sx={headerCellSx} align="right">Lines</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {margin.byCustomer.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ border: 0 }}>
                        <TableEmptyState title="No cost-captured lines" hint="No customer lines in this period carry a captured cost yet." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    margin.byCustomer.map((row) => (
                      <TableRow key={row.customer_id} sx={bodyRowSx}>
                        <TableCell>{row.company_name}</TableCell>
                        <TableCell sx={numCellSx}>{formatINR(row.revenue)}</TableCell>
                        <TableCell sx={numCellSx}>{formatINR(row.margin)}</TableCell>
                        <TableCell sx={numCellSx}>{row.marginPct === null ? '—' : formatPct(row.marginPct)}</TableCell>
                        <TableCell sx={numCellSx}>{formatNumber(row.lineCount)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </TableSurface>
        </Grid>
      </Grid>
    </Box>
  );
};

const SummaryStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
  </Box>
);

export default MarginSection;
